import React, { useState, useEffect } from 'react';
import { FaShieldAlt, FaCode, FaMousePointer, FaFingerprint, FaSave } from 'react-icons/fa';
import { fetchAPI } from '../../utils/api';

export default function AdminSettings() {
    const [settings, setSettings] = useState({
        dev_tools_restricted: '0',
        right_click_restricted: '0',
        screen_recording_watermark: '1',
        developer_mode_active: '1'
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState({ type: '', text: '' });

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const response = await fetchAPI('/admin/get_settings.php');
            setSettings(response);
        } catch (err) {
            setStatus({ type: 'error', text: 'Failed to load settings' });
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (key) => {
        setSettings(prev => ({
            ...prev,
            [key]: prev[key] === '1' ? '0' : '1'
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await fetchAPI('/admin/save_settings.php', {
                method: 'POST',
                body: JSON.stringify({ settings })
            });
            setStatus({ type: 'success', text: 'Settings saved successfully!' });
            setTimeout(() => setStatus({ type: '', text: '' }), 5000);
        } catch (err) {
            setStatus({ type: 'error', text: 'Failed to save: ' + err.message });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading Security Configurations...</div>;

    const SettingCard = ({ icon: Icon, title, description, id, value }) => (
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex items-center justify-between group hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                    <Icon size={20} />
                </div>
                <div>
                    <h3 className="font-bold text-gray-900">{title}</h3>
                    <p className="text-xs text-gray-500 font-medium">{description}</p>
                </div>
            </div>
            <button
                onClick={() => handleToggle(id)}
                className={`w-14 h-8 rounded-full transition-all relative ${value === '1' ? 'bg-blue-600' : 'bg-gray-200'}`}
            >
                <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-sm transition-all ${value === '1' ? 'left-7' : 'left-1'}`}></div>
            </button>
        </div>
    );

    return (
        <div className="max-w-4xl space-y-8 animated fadeIn">
            <div>
                <h2 className="text-3xl font-bold text-gray-900 lemon-milk tracking-tight flex items-center gap-3">
                    <FaShieldAlt className="text-blue-600" /> Security Settings
                </h2>
                <p className="text-gray-500 text-sm font-medium">Manage platform-wide security restrictions and protections</p>
            </div>

            {status.text && (
                <div className={`p-4 rounded-2xl flex items-center gap-3 animate-fade-in ${status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                    <div className={`w-2 h-2 rounded-full ${status.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <p className="text-sm font-bold">{status.text}</p>
                </div>
            )}

            <div className="grid gap-4">
                <SettingCard
                    icon={FaCode}
                    title="Developer Options Restriction"
                    description="Disable F12, Inspect Element, and Ctrl+Shift+I globally"
                    id="dev_tools_restricted"
                    value={settings.dev_tools_restricted}
                />
                <SettingCard
                    icon={FaMousePointer}
                    title="Right-Click Restriction"
                    description="Prevent users from opening the browser context menu"
                    id="right_click_restricted"
                    value={settings.right_click_restricted}
                />
                <SettingCard
                    icon={FaFingerprint}
                    title="Anti-Recording Watermark"
                    description="Show semi-transparent student identity over video content"
                    id="screen_recording_watermark"
                    value={settings.screen_recording_watermark}
                />
                <div className="mt-4 p-6 bg-blue-50/50 rounded-3xl border border-blue-100/50 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center text-blue-600">
                            <FaShieldAlt size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-blue-900">Developer Master Mode</h3>
                            <p className="text-xs text-blue-700/70 font-medium">Global switch for all security measures</p>
                        </div>
                    </div>
                    <button
                        onClick={() => handleToggle('developer_mode_active')}
                        className={`w-14 h-8 rounded-full transition-all relative ${settings.developer_mode_active === '1' ? 'bg-blue-600' : 'bg-gray-300'}`}
                    >
                        <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-sm transition-all ${settings.developer_mode_active === '1' ? 'left-7' : 'left-1'}`}></div>
                    </button>
                </div>
            </div>

            <div className="flex justify-end pt-4">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-gray-900 text-white px-8 py-3 rounded-2xl font-bold flex items-center gap-2 hover:bg-gray-800 transition-all shadow-xl active:scale-95 disabled:opacity-50"
                >
                    {saving ? 'Saving...' : <><FaSave /> Save Security Config</>}
                </button>
            </div>

            <div className="bg-yellow-50 p-6 rounded-3xl border border-yellow-100">
                <h4 className="flex items-center gap-2 text-yellow-800 font-bold mb-2">
                    <FaShieldAlt size={16} /> Admin Guidance
                </h4>
                <p className="text-xs text-yellow-700 leading-relaxed">
                    Changes made here will affect all users (Sudents and Moderators) in real-time.
                    Developer restrictions are designed to protect your unique educational content from being easily downloaded or inspected.
                    If you need to perform debugging, remember to temporarily disable these settings.
                </p>
            </div>
        </div>
    );
}
